import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { change } from 'redux-form';
import { TextField, CheckboxField, Label } from 'src/jbc-front/components/Form';
import selector from 'src/utils/Utils';
import { CLIENT_ALLOWANCE_FORM } from 'src/constants/FormNames';
import { getRadioOptions } from 'src/reducers/selectOptions';
import { getAllowanceFormValues } from 'src/reducers/clients/allowances';
import { isOtherTax } from 'src/utils/CustomValidators';

import styles from './TargetSettings.scss';
import CustomRadioField from './CustomRadioField';

const TargetSettings = ({ allowanceType, allowanceAmountType, formulaOrderType, useYearEndAdjustment, isTaxableTarget, incomeTaxTypes, dispatch }) => {
  // 「基本給」「通勤手当」以外のとき表示する項目
  const isShownNotForBaseSalaryAndCommuting = formulaOrderType !== 'base_salary' && formulaOrderType !== 'commuting';
  // 支給項目
  const isPaymentAllowance = allowanceType === 'payment';
  // 控除項目
  const isDeductionAllowance = allowanceType === 'deduction';
  // 年末調整精算用の控除項目
  const isYearEndAdjustment = isDeductionAllowance && useYearEndAdjustment;

  const isProvidentFund = formulaOrderType === 'provident_fund';

  const { t } = useTranslation();

  const [isOtherTaxRequired, setIsOtherTaxRequired] = useState(false);
  const selectIncomeTaxType = state => state.form[CLIENT_ALLOWANCE_FORM]?.values?.incomeTaxType;
  const incomeTaxType = useSelector(selectIncomeTaxType);
  const selectAllowanceId = state => state.form[CLIENT_ALLOWANCE_FORM]?.values?.id;
  const allowanceId = useSelector(selectAllowanceId);
  const handleIncomeTaxTypeChange = element => {
    const selectedValue = element.target.value;
    setIsOtherTaxRequired(selectedValue === 'other_tax');
    dispatch(change(CLIENT_ALLOWANCE_FORM, 'incomeTaxType', selectedValue));
    if (selectedValue === 'progressive_tax') {
      dispatch(change(CLIENT_ALLOWANCE_FORM, 'otherTaxRate', '0.0'));
    }
  };

  useEffect(
    () => {
      if (incomeTaxType === 'progressive_tax') {
        setIsOtherTaxRequired(false);
      } else if (incomeTaxType === 'other_tax') {
        setIsOtherTaxRequired(true);
      }
    },
    [incomeTaxType]
  );

  return (
    <div>
      <Label text={t('features.ClientAllowances.components.TargetSettings.Detailed_Setting')} />
      {allowanceAmountType === 'formula' &&
        !isYearEndAdjustment && (
          <div>
            <CheckboxField
              name="isNegativePermitted"
              label={t('features.ClientAllowances.components.TargetSettings.If_the_calculation_results_in_a_negative_amount_the_result_will_be_shown_as_0_vnd')}
              normalize={value => !!value}
            />
          </div>
        )}

      {formulaOrderType !== 'commuting' && (
        <div>
          <CheckboxField
            name="isTaxableTarget"
            label={t('features.ClientAllowances.components.TargetSettings.Subject_to_income_tax_calculation')}
            normalize={value => !!value}
            disabled={isProvidentFund}
          />
        </div>
      )}

      {isTaxableTarget && (
        <div className={styles.child_indent}>
          <CustomRadioField name="incomeTaxType" options={incomeTaxTypes} onChange={handleIncomeTaxTypeChange} />
          {isOtherTaxRequired && (
            <div className={styles.text_field}>
              <TextField
                label={t('features.ClientAllowances.components.TargetSettings.other_tax_rate')}
                name="otherTaxRate"
                required={isOtherTaxRequired}
                width="100"
                textAlign="right"
                {...isOtherTaxRequired && { validate: isOtherTax }}
              />
              <div className={styles.percent}>(%)</div>
            </div>
          )}
        </div>
      )}

      {isPaymentAllowance && (
        <div>
          <div>
            <CheckboxField
              name="isSscCalculation"
              label={t('features.ClientAllowances.components.TargetSettings.Subject_to_Social_Security_calculation')}
              normalize={value => !!value}
            />
          </div>
          {isShownNotForBaseSalaryAndCommuting && (
            <React.Fragment>
              <div>
                <CheckboxField
                  name="isReduced"
                  label={t('features.ClientAllowances.components.TargetSettings.Minus_calculation_items')}
                  normalize={value => !!value}
                  note={
                    <div>
                      {t(
                        'features.ClientAllowances.components.TargetSettings.This_will_appear_on_the_provision_table_in_the_payslip_but_you_can_check_the_box_to_deduct_this_amount_from_the_payment_amount_generally_deduction_for_absences_and_late_arrivals_are_categorized_here'
                      )}
                    </div>
                  }
                />
              </div>
            </React.Fragment>
          )}
          {/* 
          @TODO: Because this option is not currently needed in Vietnam and will be temporarily hidden. 
          https://donuts-vn.atlassian.net/browse/JBC-75
          <div>
            <CheckboxField
              name="isInKindPayment"
              label={t('features.ClientAllowances.components.TargetSettings.In_kind_Payment')}
              normalize={value => !!value}
            />
          </div> */}
        </div>
      )}

      {isDeductionAllowance && (
        <div>
          <CheckboxField
            name="isSscCalculation"
            label={t('features.ClientAllowances.components.TargetSettings.Subject_to_Social_Security_calculation')}
            normalize={value => !!value}
            disabled={isProvidentFund}
          />
        </div>
      )}

      <div>
        <CheckboxField
          name="notDisplay0YenOnPayslip"
          label={t('features.ClientAllowances.components.TargetSettings.Hide_in_payslip_if_the_amount_is_0_vnd')}
          normalize={value => !!value}
        />
      </div>

      {isShownNotForBaseSalaryAndCommuting &&
        !!allowanceId && (
        <div>
          <CheckboxField
            name="isInDisuse"
            label={t('features.ClientAllowances.components.TargetSettings.Do_not_use_this_item_Hide_setting')}
            normalize={value => !!value}
            note={
              <div>
                {t('features.ClientAllowances.components.TargetSettings.When_checked_it_will_be_changed_as_below')}
                <ul className={styles.list}>
                  <li>{t('features.ClientAllowances.components.TargetSettings.This_will_be_hidden_from_the_monthly_payslip')}</li>
                  <li>{t('features.ClientAllowances.components.TargetSettings.Can_no_longer_be_uploaded_as_provision_deduction_items_using_CSV')}</li>
                </ul>
              </div>
            }
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  incomeTaxTypes: getRadioOptions(state, 'incomeTaxTypes'),
  allowanceAmountType: selector(getAllowanceFormValues(state), 'allowanceAmountType'),
  useAsSocialInsurances: getRadioOptions(state, 'useAsSocialInsurances'),
  formulaOrderType: selector(getAllowanceFormValues(state), 'formulaOrderType'),
  isCompensationTarget: selector(getAllowanceFormValues(state), 'isCompensationTarget'),
  useYearEndAdjustment: selector(getAllowanceFormValues(state), 'useYearEndAdjustment'),
  isTaxableTarget: selector(getAllowanceFormValues(state), 'isTaxableTarget'),
  incomeTaxType: selector(getAllowanceFormValues(state), 'incomeTaxType'),
  isAnnualTaxEstimation: selector(getAllowanceFormValues(state), 'isAnnualTaxEstimation'),
  isReduced: selector(getAllowanceFormValues(state), 'isReduced'),
  kt20Types: getRadioOptions(state, 'kt20Types')
});

export default connect(mapStateToProps)(TargetSettings);
